// "What next?" data, kept apart from its screen so the sidebar and Home can show
// a count without loading the whole component.
import { api } from "./api.ts";

export type NextKind =
	| "setup"
	| "requests"
	| "registrations"
	| "forms"
	| "documents"
	| "rtw_confirm"
	| "stopped"
	| "unfilled"
	| "offers"
	| "hours"
	| "approvals"
	| "queried"
	| "pricing"
	| "rates"
	| "payroll"
	| "pension"
	| "expiring";

export interface NextItem {
	kind: NextKind;
	count: number;
	week?: string;
	examples: { id: string; title: string; detail: string | null; href: string }[];
}
export interface NextView {
	items: NextItem[];
	kinds: { kind: NextKind; label: string; shown: boolean }[];
	canChoose: boolean;
}

export const nextQuery = { queryKey: ["next"], queryFn: () => api<NextView>("/next") };

const plural = (n: number, one: string, many: string) => `${n.toLocaleString("en-GB")} ${n === 1 ? one : many}`;

/** What each kind says, in a sentence, for a count. */
export const NEXT_TITLE: Record<NextKind, (n: number) => string> = {
	setup: (n) => `${n} ${n === 1 ? "step" : "steps"} to get started`,
	requests: (n) => `${plural(n, "booking", "bookings")} asked for by clients, waiting for you to look`,
	registrations: (n) => `${plural(n, "new person has", "new people have")} registered`,
	forms: (n) => `${plural(n, "form has", "forms have")} come back`,
	documents: (n) => `${plural(n, "document", "documents")} to check`,
	rtw_confirm: (n) => `${plural(n, "right to work result", "right to work results")} from GOV.UK to confirm`,
	stopped: (n) => `${plural(n, "person has", "people have")} come off shifts because a document ran out`,
	unfilled: (n) => `${plural(n, "shift", "shifts")} in the next two weeks with nobody booked`,
	offers: (n) => `${plural(n, "shift is", "shifts are")} offered and waiting for an answer`,
	hours: (n) => `${plural(n, "person hasn't", "people haven't")} sent last week's hours`,
	approvals: (n) => `${plural(n, "timesheet is", "timesheets are")} waiting for the client to approve`,
	queried: (n) => `${plural(n, "timesheet has", "timesheets have")} been queried by the client`,
	pricing: (n) => `${plural(n, "timesheet", "timesheets")} can't be priced yet`,
	rates: (n) => `${plural(n, "rate card is", "rate cards are")} waiting for a second person to approve`,
	payroll: (n) => `${plural(n, "approved timesheet is", "approved timesheets are")} ready for payroll`,
	pension: (n) => `${plural(n, "person reaches", "people reach")} 12 weeks: tell payroll for the pension check`,
	expiring: (n) => `${plural(n, "person has", "people have")} a document running out in the next 30 days`,
};

/** One sentence for Home: the most important thing waiting (getting started first) and how much else. */
export function nextSummary(items: NextItem[]): string | null {
	const top = items.find((i) => i.kind === "setup") ?? items[0];
	if (!top) return null;
	const rest = items.length - 1;
	return `${NEXT_TITLE[top.kind](top.count)}${rest ? `, and ${rest} more ${rest === 1 ? "thing" : "things"}` : ""}`;
}
